/* eslint-disable @typescript-eslint/no-unused-vars */
// InstructionEditorV3.tsx
import React, { useEffect, useRef, useState } from "react";

type Props = {
  initialText: string;
  onSubmit: (finalText: string) => void;
  onCancel?: (currentDraft: string) => void;
  disabled?: boolean;
  submitLabel?: string;
  maxChars?: number;
  styleTokens?: {
    fontFamily?: string;   // default 'Montserrat'
    color?: string;        // default '#030303'
    borderColor?: string;  // default 'rgba(3,3,3,0.12)'
    focusRing?: string;    // default 'rgba(3,3,3,0.25)'
    bg?: string;           // default 'transparent'
    buttonBg?: string;     // default '#030303'
    buttonText?: string;   // default '#ffffff'
  };
  style?: React.CSSProperties;
  title?: string;
};

export default function InstructionEditorV3({
  initialText,
  onSubmit,
  onCancel,
  disabled,
  submitLabel = "Save",
  maxChars = 20000,
  styleTokens,
  style,
  title = "Bot Instructions",
}: Props) {
  const tokens = {
    fontFamily: "Montserrat",
    color: "#030303",
    borderColor: "rgba(3,3,3,0.12)",
    focusRing: "rgba(3,3,3,0.25)",
    bg: "transparent",
    buttonBg: "#030303",
    buttonText: "#ffffff",
    ...styleTokens,
  };

  const editorRef = useRef<HTMLDivElement>(null);

  // ✅ Local source of truth for what's shown
  const [currentText, setCurrentText] = useState(initialText);
  const [editing, setEditing] = useState(false);
  const [charCount, setCharCount] = useState(initialText.length);

  // Track the last prop value; only sync when the PROP actually changes
  const lastPropRef = useRef(initialText);
  const savedRef = useRef(initialText); // last committed text

  // Fill DOM once on mount from currentText
  useEffect(() => {
    if (!editorRef.current) return;
    setDomFromText(currentText);
  }, [currentText]); // mount

  // 🔒 Sync from parent ONLY when the *prop changes*, and only when not editing
  useEffect(() => {
    if (editing) return;
    if (lastPropRef.current !== initialText) {
      lastPropRef.current = initialText;
      savedRef.current = initialText;
      setCurrentText(initialText);
      setDomFromText(initialText);
      setCharCount(initialText.length);
    }
  }, [initialText, editing]);

  // --- DOM helpers ---
  function setDomFromText(text: string) {
    const el = editorRef.current!;
    el.innerHTML = "";
    const lines = (text || "").replace(/\r\n?/g, "\n").split("\n");
    for (let i = 0; i < lines.length; i++) {
      if (i) el.appendChild(document.createElement("br"));
      el.appendChild(document.createTextNode(lines[i]));
    }
  }

  function insertNodeAtSelection(node: Node) {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    range.insertNode(node);
    const r = document.createRange();
    r.setStartAfter(node);
    r.setEndAfter(node);
    sel.removeAllRanges();
    sel.addRange(r);
  }

  function insertTextAtCaret(text: string) {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const tn = document.createTextNode(text);
    range.insertNode(tn);
    const r = document.createRange();
    r.setStart(tn, tn.length);
    r.setEnd(tn, tn.length);
    sel.removeAllRanges();
    sel.addRange(r);
  }

  function readText(): string {
    const el = editorRef.current;
    if (!el) return savedRef.current;
    return (el.innerText || "")
      .replace(/\u00A0/g, " ")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n");
  }

  function setCaretToEnd(el: HTMLElement) {
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    sel.addRange(range);
  }

  // --- Handlers ---
  function handleInput() {
    const t = readText();
    if (t.length > maxChars) {
      const truncated = t.slice(0, maxChars);
      setDomFromText(truncated);
      setCaretToEnd(editorRef.current!);
      setCharCount(truncated.length);
    } else {
      setCharCount(t.length);
    }
  }

  function handleBeforeInput(e: React.FormEvent<HTMLDivElement> & any) {
    if (!editing || disabled) return;
    const type = e.nativeEvent?.inputType;

    if (type === "insertParagraph") {
      e.preventDefault();
      const br = document.createElement("br");
      insertNodeAtSelection(br);
      setCharCount(readText().length);
    } else if (type === "insertFromPaste" || type === "insertFromDrop") {
      e.preventDefault();
      const data: string =
        e.nativeEvent?.dataTransfer?.getData("text/plain") ??
        e.nativeEvent?.clipboardData?.getData("text/plain") ??
        "";
      const normalized = data.replace(/\r\n?/g, "\n");
      const parts = normalized.split("\n");
      parts.forEach((p, i) => {
        if (i) insertNodeAtSelection(document.createElement("br"));
        if (p) insertTextAtCaret(p);
      });
      setCharCount(readText().length);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      doSubmit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      doCancel();
    } else if (e.key === "Enter") {
      // Fallback for browsers that don't send insertParagraph to beforeinput
      e.preventDefault();
      const br = document.createElement("br");
      insertNodeAtSelection(br);
      setCharCount(readText().length);
    }
  }

  function startEditing() {
    if (disabled || editing) return;
    setEditing(true);
    requestAnimationFrame(() => editorRef.current?.focus());
  }

  function doSubmit() {
    if (disabled) return;
    const finalText = readText().trim();
    savedRef.current = finalText;
    setCurrentText(finalText);           // ✅ keep local state in sync
    setDomFromText(finalText);           // ✅ ensure DOM shows what we saved
    setCharCount(finalText.length);
    setEditing(false);
    onSubmit(finalText);                 // parent can update prop later; we won't revert meanwhile
  }

  function doCancel() {
    setEditing(false);
    setDomFromText(savedRef.current);
    setCharCount(savedRef.current.length);
    onCancel?.(savedRef.current);
  }

  // --- Styles ---
  const containerStyle: React.CSSProperties = {
    ...style,
    fontFamily: tokens.fontFamily,
    color: tokens.color,
  };
  const headerRow: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  };
  const hintStyle: React.CSSProperties = {
    fontSize: 12,
    lineHeight: "16px",
    color: tokens.color,
    opacity: 0.7,
  };
  const actionsStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
  };
  const counterStyle: React.CSSProperties = {
    fontSize: 12,
    lineHeight: "16px",
    opacity: 0.7,
    fontFamily: tokens.fontFamily,
  };
  const btnBase: React.CSSProperties = {
    fontFamily: tokens.fontFamily,
    fontSize: 14,
    lineHeight: "18px",
    padding: "8px 12px",
    borderRadius: 8,
    cursor: "pointer",
    border: `1px solid ${tokens.borderColor}`,
    background: "#fff",
    color: tokens.color,
  };
  const btnPrimary: React.CSSProperties = {
    ...btnBase,
    background: 'var(--primary)',
    color: '#F0F0F0',
    border: "none",
  };
  const editorStyle: React.CSSProperties = {
    minHeight: 360,
    padding: 16,
    borderRadius: 12,
    border: `1px solid ${tokens.borderColor}`,
    background: tokens.bg,
    outline: "none",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: tokens.fontFamily,
    fontSize: 16,
    lineHeight: "24px",
    overflow: "auto",
  };

  return (
    <div style={containerStyle}>
      <div style={headerRow}>
        <div style={hintStyle}>
          {editing ? "Editing (Esc to cancel, ⌘/Ctrl+S to save)" : title}
        </div>
        <div style={actionsStyle}>
          <span style={counterStyle}>
            {charCount.toLocaleString()}/{maxChars.toLocaleString()}
          </span>
          {editing ? (
            <>
              <button type="button" style={btnBase} onClick={doCancel} disabled={disabled}>
                Cancel
              </button>
              <button type="button" style={btnPrimary} onClick={doSubmit} disabled={disabled}>
                {submitLabel}
              </button>
            </>
          ) : (
            <button type="button" style={btnBase} onClick={startEditing} disabled={disabled}>
              Edit
            </button>
          )}
        </div>
      </div>

      <div
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        tabIndex={0}
        contentEditable={!disabled && editing}
        suppressContentEditableWarning
        onClick={startEditing}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onBeforeInput={handleBeforeInput as any}
        style={editorStyle}
        onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 3px ${tokens.focusRing}`)}
        onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
      />
      <div style={{ ...hintStyle, marginTop: 6 }}>
        Enter inserts line breaks without scroll. Paste is plain text. Save with ⌘/Ctrl+S.
      </div>
    </div>
  );
}
